/**
 * 图表引擎的 TypeScript 类型封装。
 *
 * 引擎本体（charts.engine.js）是从原型逐行移植的零依赖 SVG 渲染代码，
 * 此处只做两件事：
 *   1. 给它补上类型（原文件是 ES5 JS，无类型）；
 *   2. 把语义色键（'h' | 'e' | 'w' …）解析成具体颜色后再传入，
 *      这样图表不再依赖「必须处在 :root 令牌生效的 DOM 上下文里」这一隐藏约束。
 */
import './charts.engine.js';
import type { ThemeColorKey } from '@hew3/shared';
import { resolveColor } from '@hew3/shared';

export interface HelixOptions {
  h?: number;
  e?: number;
  w?: number;
  syn?: number;
  w_?: number;
  h_?: number;
}

export interface RadarOptions {
  axes?: { label: string; value: number }[];
  max?: number;
  size?: number;
  color?: string;
  ref?: number[] | null;
}

export interface SparkOptions {
  data?: number[];
  w?: number;
  h?: number;
  color?: string;
  fill?: boolean;
  dots?: boolean;
}

export interface RingOptions {
  value?: number;
  max?: number;
  size?: number;
  thickness?: number;
  color?: string;
  label?: string;
  unit?: string;
}

export interface DonutOptions {
  segments?: { value: number; color: string }[];
  size?: number;
  thickness?: number;
}

export interface HBarItem {
  label: string;
  value: number;
  max?: number;
  color?: string;
  text?: string;
  sub?: string;
}

export interface VBarItem {
  label: string;
  value: number;
  unit?: string;
  color?: string;
}

export interface ChainNode {
  icon: string;
  label: string;
  value: string;
  color?: string;
  bg?: string;
  bd?: string;
}

interface ChartsApi {
  helix: (o: HelixOptions) => string;
  radar: (o: RadarOptions) => string;
  spark: (o: SparkOptions) => string;
  ring: (o: RingOptions) => string;
  donut: (o: DonutOptions) => string;
  hbars: (o: { items: HBarItem[] }) => string;
  vbars: (o: { items: VBarItem[]; h?: number }) => string;
  chain: (o: { nodes: ChainNode[]; risk?: number }) => string;
}

function engine(): ChartsApi {
  const api = (globalThis as unknown as { HW3?: { charts?: ChartsApi } }).HW3?.charts;
  if (!api) throw new Error('图表引擎未加载：请确认 lib/charts.engine.js 已被引入');
  return api;
}

/** 语义色键 → 引擎可用的颜色字符串 */
export const color = (key: ThemeColorKey): string => resolveColor(key);
export const helix = (o: HelixOptions): string => engine().helix(o);

export const radar = (o: RadarOptions): string =>
  engine().radar({ ...o, color: o.color ? resolveColor(o.color as ThemeColorKey) : undefined });

export const spark = (o: SparkOptions): string =>
  engine().spark({ ...o, color: o.color ? resolveColor(o.color as ThemeColorKey) : undefined });

export const ring = (o: RingOptions): string =>
  engine().ring({ ...o, color: o.color ? resolveColor(o.color as ThemeColorKey) : undefined });

export const donut = (o: DonutOptions): string =>
  engine().donut({
    ...o,
    segments: (o.segments ?? []).map((s) => ({ ...s, color: resolveColor(s.color as ThemeColorKey) })),
  });

export const hbars = (o: { items: HBarItem[] }): string =>
  engine().hbars({
    items: o.items.map((i) => ({ ...i, color: i.color ? resolveColor(i.color as ThemeColorKey) : undefined })),
  });

export const vbars = (o: { items: VBarItem[]; h?: number }): string =>
  engine().vbars({
    ...o,
    items: o.items.map((i) => ({ ...i, color: i.color ? resolveColor(i.color as ThemeColorKey) : undefined })),
  });

export const chain = (o: { nodes: ChainNode[]; risk?: number }): string =>
  engine().chain({
    ...o,
    nodes: o.nodes.map((n) => ({
      ...n,
      color: n.color ? resolveColor(n.color as ThemeColorKey) : undefined,
    })),
  });

/**
 * 命名空间对象：约定文档第五节与样板页（HomePage）都按 `charts.helix(...)` 调用，
 * 这里补一个聚合导出，使 `import { charts } from '../../lib/charts.ts'` 可用。
 * （原先只导出了单个函数，HomePage 的 `charts.helix` 会直接编译失败。）
 */
export const charts = {
  helix,
  radar,
  spark,
  ring,
  donut,
  hbars,
  vbars,
  chain,
};

